import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { submitStudent, bulkUpload, getProjectInfo, getPublicStudents, updatePublicStudent, publicStudentPhotoUrl } from '../api';
import { ALL_FIELDS, FIELD_TO_API, maxLenFor } from '../fields';
import logoImage from '../image/printbanglalogo.png';

export default function ClientUpload() {
  const { token } = useParams();
  const [tab, setTab] = useState('single');

  // Project info
  const [projectInfo, setProjectInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState('');

  // Single form
  const [form, setForm] = useState({});
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [error, setError] = useState('');
  const photoRef = useRef();

  // Entries list
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [showEntries, setShowEntries] = useState(false);

  // Inline editing
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Bulk form
  const [csvFile, setCsvFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const csvRef = useRef();
  const zipRef = useRef();

  useEffect(() => {
    getProjectInfo(token)
      .then((r) => {
        setProjectInfo(r.data);
        const initial = {};
        ALL_FIELDS.forEach((f) => {
          if (!r.data.enabled_fields || r.data.enabled_fields[f.key]) {
            initial[f.key] = '';
          }
        });
        setForm(initial);
      })
      .catch(() => setInfoError('This upload link is invalid or expired.'))
      .finally(() => setInfoLoading(false));
  }, [token]);

  const enabledFields = projectInfo?.enabled_fields;
  const activeFields = ALL_FIELDS.filter((f) => !enabledFields || enabledFields[f.key]);

  const loadEntries = useCallback(() => {
    setEntriesLoading(true);
    getPublicStudents(token)
      .then((r) => setEntries(r.data))
      .catch(() => {})
      .finally(() => setEntriesLoading(false));
  }, [token]);

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditError('');
    const form = {};
    activeFields.forEach((f) => {
      const apiKey = f.key === 'student_name' ? 'name' : f.key;
      form[apiKey] = entry[apiKey] || '';
    });
    setEditForm(form);
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); setEditError(''); };

  const saveEdit = async (entryId) => {
    setEditSaving(true);
    setEditError('');
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (k === 'name') payload.name = v;
        else payload[k] = v;
      });
      const res = await updatePublicStudent(token, entryId, payload);
      setEntries((prev) => prev.map((e) => e.id === entryId ? { ...e, ...res.data } : e));
      setEditingId(null);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Save failed.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleSingle = async (e) => {
    e.preventDefault();
    const hasRequiredFields = activeFields.every((f) => {
      if (!['student_name', 'student_id'].includes(f.key)) return true;
      return (form[f.key] || '').trim().length > 0;
    });
    if (!hasRequiredFields) return;
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      activeFields.forEach((f) => {
        const apiKey = FIELD_TO_API[f.key];
        const val = form[f.key] || '';
        if (val.trim()) fd.append(apiKey, val.trim());
      });
      if (photo) fd.append('photo', photo);
      await submitStudent(token, fd);
      setSubmittedData({ ...form, photoName: photo?.name, photoUrl: photo ? URL.createObjectURL(photo) : null });
      setProjectInfo((prev) => prev ? { ...prev, student_count: (prev.student_count || 0) + 1 } : prev);
      loadEntries();
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulk = async (e) => {
    e.preventDefault();
    if (!csvFile) return;
    setBulkLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('csv_file', csvFile);
      if (zipFile) fd.append('zip_file', zipFile);
      const res = await bulkUpload(token, fd);
      setBulkResult(res.data);
      loadEntries();
    } catch (err) {
      setError(err.response?.data?.detail || 'Bulk upload failed.');
    } finally {
      setBulkLoading(false);
    }
  };

  const resetSingle = () => {
    const initial = {};
    activeFields.forEach((f) => { initial[f.key] = ''; });
    setForm(initial);
    setPhoto(null);
    setSubmittedData(null);
    setError('');
  };

  const csvColumns = activeFields.map((f) => FIELD_TO_API[f.key]).join(', ') + ', photo';

  if (infoLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (infoError) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}>
        <div className="card" style={{ maxWidth: 400, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠</div>
          <h2 style={{ fontWeight: 600, marginBottom: 8 }}>Invalid Link</h2>
          <p className="text-sm text-muted">{infoError}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(110,231,183,0.06) 0%, transparent 100%), var(--bg)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '40px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src={logoImage}
            alt="Company logo"
            style={{ width: 120, height: 'auto', margin: '0 auto 14px', display: 'block' }}
          />
          <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 520, margin: '0 auto 12px' }}>
            <p style={{ margin: '0 0 6px' }}>হাউজ 229, ফকিরাপুল গরম পানির গলি, মতিঝিল, ঢাকা-১০০০, Dhaka, Bangladesh, 1000</p>
            <p style={{ margin: '0 0 6px' }}>Phone: <a href="tel:01619995154" style={{ color: 'inherit', textDecoration: 'underline' }}>01619-995154</a></p>
            <p style={{ margin: 0 }}>Email: <a href="mailto:printbangla5154@gmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>printbangla5154@gmail.com</a></p>
          </div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            {projectInfo.name}
          </h1>
          {projectInfo.description && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 400, margin: '0 auto 8px' }}>
              {projectInfo.description}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span className="badge badge-gray" style={{ fontSize: '0.8em' }}>
              {projectInfo.student_count} {projectInfo.student_count === 1 ? 'entry' : 'entries'} submitted
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 4, marginBottom: 20 }}>
          {['single', 'bulk'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="btn"
              style={{
                flex: 1, justifyContent: 'center', borderRadius: 6,
                background: tab === t ? 'var(--bg3)' : 'transparent',
                color: tab === t ? 'var(--text)' : 'var(--text3)',
                border: 'none', padding: '7px',
              }}
            >
              {t === 'single' ? 'Single Entry' : 'Bulk Upload'}
            </button>
          ))}
        </div>

        <div className="card">
          {tab === 'single' && (
            <>
              {submittedData ? (
                <SubmissionPreview
                  data={submittedData}
                  activeFields={activeFields}
                  onReset={resetSingle}
                />
              ) : (
                <form onSubmit={handleSingle} className="stack stack-3">
                  {error && <div className="alert alert-error">{error}</div>}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                    {activeFields.map((f) => (
                      <div
                        key={f.key}
                        className="form-group"
                        style={{
                          gridColumn: f.key === 'address' ? '1 / -1' : 'auto',
                          marginBottom: 0,
                        }}
                      >
                        <label className="form-label">
                          {f.label}
                          {['student_name', 'student_id'].includes(f.key) && ' *'}
                        </label>
                        <input
                          className="form-input"
                          value={form[f.key] || ''}
                          maxLength={maxLenFor(f.key)}
                          onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                          required={['student_name', 'student_id'].includes(f.key)}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Photo</label>
                    <div className="upload-zone" style={{ padding: 20 }} onClick={() => photoRef.current.click()}>
                      {photo ? (
                        <p style={{ color: 'var(--accent)' }}>📷 {photo.name}</p>
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M14.5 4h-5L8 6H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2h-4l-1.5-2z"/></svg>
                          <p>Upload your photo</p>
                          <span>JPG or PNG, max 5MB</span>
                        </>
                      )}
                      <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png" style={{ display: 'none' }} onChange={(e) => setPhoto(e.target.files[0])} />
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg w-full" disabled={submitting} style={{ justifyContent: 'center' }}>
                    {submitting ? <><span className="spinner" /> Submitting…</> : 'Submit Entry'}
                  </button>
                </form>
              )}
            </>
          )}

          {tab === 'bulk' && (
            <form onSubmit={handleBulk} className="stack stack-3">
              {error && <div className="alert alert-error">{error}</div>}
              {bulkResult ? (
                <div>
                  <div className="alert alert-success" style={{ marginBottom: 12 }}>
                    ✓ {bulkResult.created} entries added, {bulkResult.failed} failed
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: 12 }}>
                      <div className="form-label" style={{ marginBottom: 6 }}>Errors</div>
                      {bulkResult.errors.map((e, i) => (
                        <div key={i} className="text-sm" style={{ color: 'var(--red)', fontFamily: 'var(--mono)' }}>{e}</div>
                      ))}
                    </div>
                  )}
                  <button className="btn btn-ghost btn-sm mt-4" onClick={() => { setBulkResult(null); setCsvFile(null); setZipFile(null); }}>
                    Upload Another Batch
                  </button>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">CSV File *</label>
                    <div className="upload-zone" style={{ padding: 20 }} onClick={() => csvRef.current.click()}>
                      {csvFile ? <p style={{ color: 'var(--accent)' }}>📄 {csvFile.name}</p> : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/></svg>
                          <p>Upload CSV file</p>
                          <span style={{ wordBreak: 'break-word' }}>Columns: {csvColumns}</span>
                        </>
                      )}
                      <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => setCsvFile(e.target.files[0])} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Photos ZIP (optional)</label>
                    <div className="upload-zone" style={{ padding: 20 }} onClick={() => zipRef.current.click()}>
                      {zipFile ? <p style={{ color: 'var(--accent)' }}>🗜 {zipFile.name}</p> : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2z"/><path d="M16 3H8l-2 4h12l-2-4z"/></svg>
                          <p>Upload photos ZIP</p>
                          <span>Name files as student_id.jpg (e.g. 2024001.jpg)</span>
                        </>
                      )}
                      <input ref={zipRef} type="file" accept=".zip" style={{ display: 'none' }} onChange={(e) => setZipFile(e.target.files[0])} />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary btn-lg w-full" disabled={bulkLoading || !csvFile} style={{ justifyContent: 'center' }}>
                    {bulkLoading ? <><span className="spinner" /> Uploading…</> : 'Upload Batch'}
                  </button>
                </>
              )}
            </form>
          )}
        </div>

        {/* Entries section */}
        <div style={{ marginTop: 24 }}>
          <button
            className="btn btn-ghost"
            style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}
            onClick={() => {
              if (!showEntries) loadEntries();
              setShowEntries((v) => !v);
            }}
          >
            <span style={{ fontWeight: 600 }}>View Submitted Entries ({projectInfo?.student_count || 0})</span>
            <span style={{ fontSize: 11 }}>{showEntries ? '▲' : '▼'}</span>
          </button>

          {showEntries && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {entriesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                  <div className="spinner" />
                </div>
              ) : entries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>
                  No entries submitted yet.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  {editError && (
                    <div className="alert alert-error" style={{ margin: '8px 12px 0' }}>{editError}</div>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Photo</th>
                        {activeFields.map((f) => (
                          <th key={f.key} style={thStyle}>{f.label}</th>
                        ))}
                        <th style={thStyle}>Status</th>
                        <th style={thStyle}>Submitted</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, i) => {
                        const isEditing = editingId === e.id;
                        const canEdit = e.status !== 'completed';
                        return (
                          <tr key={e.id} style={{ borderBottom: '1px solid var(--border)', background: isEditing ? 'var(--bg2)' : undefined }}>
                            <td style={tdStyle}>{i + 1}</td>
                            <td style={tdStyle}>
                              {e.has_photo ? (
                                <img
                                  src={publicStudentPhotoUrl(token, e.id)}
                                  alt=""
                                  style={{ width: 36, height: 36, borderRadius: 5, objectFit: 'cover', border: '1px solid var(--border)', display: 'block' }}
                                  onError={(ev) => { ev.currentTarget.style.display = 'none'; }}
                                />
                              ) : (
                                <span style={{ color: 'var(--text-muted)' }}>—</span>
                              )}
                            </td>
                            {activeFields.map((f) => {
                              const apiKey = f.key === 'student_name' ? 'name' : f.key;
                              return (
                                <td key={f.key} style={tdStyle}>
                                  {isEditing ? (
                                    <input
                                      className="form-input"
                                      style={{ padding: '3px 6px', fontSize: 12, minWidth: 80 }}
                                      value={editForm[apiKey] ?? ''}
                                      maxLength={maxLenFor(apiKey)}
                                      onChange={(ev) => setEditForm((p) => ({ ...p, [apiKey]: ev.target.value }))}
                                    />
                                  ) : (
                                    e[apiKey] || <span style={{ color: 'var(--text-muted)' }}>—</span>
                                  )}
                                </td>
                              );
                            })}
                            <td style={tdStyle}>
                              <span className={`badge ${e.status === 'completed' ? 'badge-green' : e.status === 'failed' ? 'badge-red' : 'badge-gray'}`}>
                                {e.status}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                              {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                              {isEditing ? (
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    style={{ padding: '2px 8px', fontSize: 11 }}
                                    disabled={editSaving}
                                    onClick={() => saveEdit(e.id)}
                                  >
                                    {editSaving ? '…' : 'Save'}
                                  </button>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    style={{ padding: '2px 8px', fontSize: 11 }}
                                    onClick={cancelEdit}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : canEdit ? (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ padding: '2px 8px', fontSize: 11 }}
                                  onClick={() => startEdit(e)}
                                >
                                  Edit
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

          </div>
    </div>
  );
}

const thStyle = {
  padding: '8px 12px', textAlign: 'left', fontWeight: 600,
  fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--text-muted)', whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '8px 12px', color: 'var(--text)', verticalAlign: 'middle',
};

function SubmissionPreview({ data, activeFields, onReset }) {
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          background: 'rgba(52,211,153,0.15)', border: '2px solid var(--accent)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, marginBottom: 10,
        }}>✓</div>
        <h2 style={{ fontWeight: 600, color: 'var(--accent)', marginBottom: 4 }}>Submitted!</h2>
        <p className="text-sm text-muted">Your information has been received. Your ID card will be generated soon.</p>
      </div>

      {/* Card Preview */}
      <div style={{
        border: '2px solid var(--border)',
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 16,
      }}>
        <div style={{
          background: 'var(--bg2)',
          padding: '10px 16px',
          borderBottom: '1px solid var(--border)',
          fontSize: '0.78rem',
          fontWeight: 600,
          color: 'var(--text-muted)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>
          ID Card Preview
        </div>
        <div style={{ padding: 16 }}>
          {data.photoName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
              {data.photoUrl ? (
                <img
                  src={data.photoUrl}
                  alt="Your photo"
                  style={{ width: 56, height: 56, borderRadius: 6, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border)' }}
                />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: 6,
                  background: 'var(--bg3)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 20, flexShrink: 0,
                }}>📷</div>
              )}
              <div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 2 }}>Photo</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>{data.photoName}</div>
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
            {activeFields.map((f) => {
              const val = data[f.key];
              if (!val) return null;
              return (
                <div key={f.key} style={{ gridColumn: f.key === 'address' ? '1 / -1' : 'auto' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {f.label}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text)', fontFamily: f.key === 'student_id' ? 'var(--mono)' : 'inherit', wordBreak: 'break-word' }}>
                    {val}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <button className="btn btn-ghost btn-sm w-full" style={{ justifyContent: 'center' }} onClick={onReset}>
        Submit Another Entry
      </button>
    </div>
  );
}
